/**
 * Handling for stale lazy-loaded chunks after a deploy.
 *
 * Every page is code-split via React.lazy, and built chunk filenames are
 * content-hashed. When a new frontend version is deployed, the old chunks no
 * longer exist on the server — users mid-session then hit
 * "Failed to fetch dynamically imported module" when navigating to a screen
 * they haven't loaded yet, which surfaces as the app-wide error boundary.
 *
 * The remedy is a full page reload: it fetches the new index.html and the
 * matching set of chunks. A sessionStorage cooldown prevents an infinite
 * reload loop if the assets are genuinely unreachable (e.g. offline).
 */

const RELOAD_GUARD_KEY = 'routemize:stale-chunk-reload-at';
const RELOAD_COOLDOWN_MS = 30_000;

export const isStaleChunkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk \S+ failed|ChunkLoadError|Unable to preload CSS/i.test(
    message
  );
};

/**
 * Reload the page to pick up freshly deployed assets.
 * Returns false when a reload happened too recently (loop guard) so callers
 * can fall back to showing an error UI instead.
 */
export const reloadForStaleChunk = (): boolean => {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
  } catch {
    // sessionStorage unavailable (private mode / embedded) — still reload,
    // worst case the browser's own reload throttling kicks in.
  }

  if (Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS) {
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // ignore
  }

  window.location.reload();
  return true;
};
